import React, { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Brain, Cpu, Network, Activity, Bot, Zap } from 'lucide-react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
  life: number;
  maxLife: number;
}

// Floating AI Icon Component
const FloatingIcon: React.FC<{ icon: React.ComponentType<any>; delay: number }> = ({ icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ 
      opacity: [0.3, 0.6, 0.4, 0.6],
      y: [-10, 10, -10],
      x: [-5, 5, -5],
      rotate: [0, 5, -5, 0]
    }}
    transition={{ 
      duration: 4,
      delay,
      repeat: Infinity,
      repeatType: "reverse"
    }}
    className="absolute"
  >
    <Icon className="w-6 h-6 text-primary/30" />
  </motion.div>
);

export const EnhancedParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  
  // AI Icons configuration
  const icons = [Brain, Cpu, Network, Activity, Bot, Zap];
  
  const positions = [
    { top: '10%', left: '10%' },
    { top: '20%', right: '15%' },
    { top: '40%', left: '5%' },
    { top: '60%', right: '10%' },
    { top: '80%', left: '20%' },
    { top: '15%', left: '50%' },
    { top: '35%', right: '30%' },
    { top: '70%', left: '60%' },
    { top: '25%', right: '50%' },
    { top: '85%', right: '40%' },
    { top: '45%', left: '80%' },
    { top: '65%', left: '30%' },
    { top: '30%', right: '20%' },
    { top: '75%', right: '60%' },
    { top: '55%', left: '40%' }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const colors = ['#00CFFF', '#0FFFA0', '#8A3FFC'];

    const createParticle = (): Particle => {
      const edge = Math.floor(Math.random() * 4);
      let x, y;

      switch (edge) {
        case 0: // Top
          x = Math.random() * canvas.width;
          y = -10;
          break;
        case 1: // Right
          x = canvas.width + 10;
          y = Math.random() * canvas.height;
          break;
        case 2: // Bottom
          x = Math.random() * canvas.width;
          y = canvas.height + 10;
          break;
        default: // Left
          x = -10;
          y = Math.random() * canvas.height;
      }

      const maxLife = 300 + Math.random() * 200;

      return {
        x,
        y,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: maxLife,
        maxLife
      };
    };

    const updateParticles = () => {
      particlesRef.current = particlesRef.current.filter(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life--;

        // Fade out as life decreases
        particle.opacity = (particle.life / particle.maxLife) * 0.5;

        return particle.life > 0 &&
               particle.x > -20 &&
               particle.x < canvas.width + 20 &&
               particle.y > -20 &&
               particle.y < canvas.height + 20;
      });

      // Add new particles
      while (particlesRef.current.length < 80) {
        particlesRef.current.push(createParticle());
      }
    };

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      ctx.strokeStyle = 'rgba(0, 207, 255, 0.1)';
      ctx.lineWidth = 1;

      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const dx = particlesRef.current[i].x - particlesRef.current[j].x;
          const dy = particlesRef.current[i].y - particlesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            const opacity = (1 - distance / 100) * 0.1;
            ctx.globalAlpha = opacity;
            ctx.beginPath();
            ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
            ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      particlesRef.current.forEach(particle => {
        ctx.globalAlpha = particle.opacity;
        ctx.fillStyle = particle.color;
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = 10;
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.shadowBlur = 0;
      });

      ctx.globalAlpha = 1;
    };

    const animate = () => {
      updateParticles();
      drawParticles();
      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Canvas-based particle dots and connection lines */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{ background: 'transparent' }}
      />
      
      {/* AI-related floating icons */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {Array.from({ length: 15 }).map((_, i) => {
          const Icon = icons[i % icons.length];
          const position = positions[i];
          return (
            <div
              key={i}
              className="absolute"
              style={position}
            >
              <FloatingIcon 
                icon={Icon} 
                delay={i * 0.3}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};